import { __awaiter, __generator, __read, __assign, __spreadArray } from 'tslib';
import * as React from 'react';
import { CountryLocationContext } from '../localization/context.esm.js';
import { countryCodeToLocale } from '../localization/setup.esm.js';
import '../localization/utils.esm.js';
import { extractDataFromTextCopies } from '../utils/contentful.esm.js';
import { useContentfulClient, createContentfulClient } from './client.esm.js';

var LINK_LEVEL_DEPTH = 10; // https://www.contentful.com/developers/docs/references/content-delivery-api/#/reference/links/retrieval-of-linked-items
/**
 *
 * returns contentful entries
 * @template T
 * @param {ContentfulClientApi} client
 * @param {*} query
 * @returns {Promise<EntryCollection<T>>}
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getContentfulEntries(client, query) {
    return __awaiter(this, void 0, void 0, function () {
        return __generator(this, function (_a) {
            return [2 /*return*/, client.getEntries(query)];
        });
    });
}
function useContentful(entryId, entryValue) {
    var _this = this;
    // state where we store entry info (including fields)
    var _a = __read(React.useState(entryValue), 2), entry = _a[0], setEntry = _a[1];
    var countryCode = React.useContext(CountryLocationContext).countryCode;
    var contentfulClient = useContentfulClient();
    // load entry from contentful API and store it in state
    var loadEntry = React.useCallback(function (entryId) { return __awaiter(_this, void 0, void 0, function () {
        var entry_1, error_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, contentfulClient.getEntry(entryId, {
                            include: LINK_LEVEL_DEPTH,
                            locale: countryCodeToLocale[countryCode === null || countryCode === void 0 ? void 0 : countryCode.toUpperCase()],
                        })];
                case 1:
                    entry_1 = _a.sent();
                    setEntry(entry_1);
                    return [3 /*break*/, 3];
                case 2:
                    error_1 = _a.sent();
                    // eslint-disable-next-line no-console
                    console.log(error_1); // TODO better handling??
                    return [3 /*break*/, 3];
                case 3: return [2 /*return*/];
            }
        });
    }); }, [countryCode, contentfulClient]);
    // every time entryValue changes let's update state
    /**
     * Removing this effect will cause error when we change components entryValue (e.g. which menu to show)
     * without it we will always show first value selected.
     */
    React.useEffect(function () {
        setEntry(entryValue);
    }, [entryValue]);
    /**
     * On mount and if entry value changes (and we don't have entry value) let's load it from contentfull API
     */
    React.useEffect(function () {
        if (!entryValue) {
            // this IF here is we don't load EACH content model from api only these that are missing the data
            loadEntry(entryId);
        }
    }, [entryId, entryValue, loadEntry]);
    return {
        entry: entry,
        entryMeta: entry && entry.sys,
        fields: entry && entry.fields,
    };
}
function getContentfulClient(previewContentfulClient, contentfulClientCredentials) {
    if (previewContentfulClient) {
        return createContentfulClient(__assign(__assign({}, contentfulClientCredentials), { host: 'preview.contentful.com' }));
    }
    return createContentfulClient(contentfulClientCredentials);
}
function getGlobalTextCopy(_a) {
    var usePreviewClient = _a.usePreviewClient, contentfulClientCredentials = _a.contentfulClientCredentials, countryCode = _a.countryCode;
    return __awaiter(this, void 0, void 0, function () {
        var client, entries, newEntries;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    client = getContentfulClient(!!usePreviewClient, contentfulClientCredentials);
                    return [4 /*yield*/, getContentfulEntries(client, {
                            content_type: 'globalTextCopy',
                            include: 10,
                            limit: 100,
                            locale: countryCodeToLocale[countryCode === null || countryCode === void 0 ? void 0 : countryCode.toUpperCase()],
                        })];
                case 1:
                    entries = _b.sent();
                    if (entries === null || entries === void 0 ? void 0 : entries.items.length) {
                        newEntries = entries.items.reduce(function (acc, item) {
                            var _a;
                            return __assign(__assign({}, acc), (_a = {}, _a[item.fields.componentName] = extractDataFromTextCopies(item.fields.textCopies), _a));
                        }, {});
                        return [2 /*return*/, newEntries];
                    }
                    return [2 /*return*/, {}];
            }
        });
    });
}
function getPage(_a) {
    var path = _a.path, countryCode = _a.countryCode, usePreviewClient = _a.usePreviewClient, contentfulClientCredentials = _a.contentfulClientCredentials;
    return __awaiter(this, void 0, void 0, function () {
        var caseInsensitivePath, client, pageEntries;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    caseInsensitivePath = path.toLowerCase();
                    client = getContentfulClient(!!usePreviewClient, contentfulClientCredentials);
                    return [4 /*yield*/, getContentfulEntries(client, {
                            content_type: 'page',
                            'fields.path': caseInsensitivePath,
                            include: LINK_LEVEL_DEPTH,
                            locale: countryCodeToLocale[countryCode === null || countryCode === void 0 ? void 0 : countryCode.toUpperCase()],
                        })];
                case 1:
                    pageEntries = _b.sent();
                    if (pageEntries.total > 0) {
                        if (pageEntries.total > 1) {
                            // eslint-disable-next-line no-console
                            console.error('More that 1 page found with the same path');
                        }
                        return [2 /*return*/, pageEntries.items[0]];
                    }
                    return [2 /*return*/, null];
            }
        });
    });
}
function getVideoEntry(_a) {
    var _b;
    var path = _a.path, countryCode = _a.countryCode, usePreviewClient = _a.usePreviewClient, contentfulClientCredentials = _a.contentfulClientCredentials;
    return __awaiter(this, void 0, void 0, function () {
        var client, caseInsensitivePath, _c, slug, pageEntries, videoPageEntries, videoFields, openGraphImage, videoPage;
        return __generator(this, function (_d) {
            switch (_d.label) {
                case 0:
                    client = getContentfulClient(!!usePreviewClient, contentfulClientCredentials);
                    caseInsensitivePath = path.toLowerCase();
                    _c = __read(caseInsensitivePath.split('videos/'), 2), slug = _c[1];
                    if (!path || !slug || slug.length === 0) {
                        return [2 /*return*/, null];
                    }
                    return [4 /*yield*/, getContentfulEntries(client, {
                            content_type: 'page',
                            'fields.path': '/',
                            include: LINK_LEVEL_DEPTH,
                            locale: countryCodeToLocale[countryCode === null || countryCode === void 0 ? void 0 : countryCode.toUpperCase()],
                        })];
                case 1:
                    pageEntries = _d.sent();
                    return [4 /*yield*/, getContentfulEntries(client, {
                            content_type: 'video',
                            'fields.slug': slug,
                            include: LINK_LEVEL_DEPTH,
                            locale: countryCodeToLocale[countryCode === null || countryCode === void 0 ? void 0 : countryCode.toUpperCase()],
                        })];
                case 2:
                    videoPageEntries = _d.sent();
                    if (videoPageEntries.total === 0 || pageEntries.total === 0) {
                        return [2 /*return*/, null];
                    }
                    if (videoPageEntries.total > 1 || pageEntries.total > 1) {
                        // eslint-disable-next-line no-console
                        console.error('More that 1 page found with the same path');
                    }
                    videoFields = videoPageEntries.items[0].fields;
                    openGraphImage = (_b = videoPageEntries.items[0].fields.thumbnail) === null || _b === void 0 ? void 0 : _b.fields.asset;
                    videoPage = __assign(__assign({}, pageEntries.items[0]), { fields: __assign(__assign(__assign(__assign(__assign({}, pageEntries.items[0].fields), ((videoFields === null || videoFields === void 0 ? void 0 : videoFields.heading) && {
                            openGraphTitle: videoFields.heading,
                            tabTitle: videoFields.heading,
                        })), ((videoFields === null || videoFields === void 0 ? void 0 : videoFields.body) && {
                            metaDescription: videoFields.body,
                            openGraphDescription: videoFields.body,
                        })), (openGraphImage && { openGraphImage: openGraphImage })), { sections: [videoPageEntries.items[0]] }) });
                    return [2 /*return*/, videoPage];
            }
        });
    });
}
function getWebinarsEntry(_a) {
    var path = _a.path, countryCode = _a.countryCode, usePreviewClient = _a.usePreviewClient, contentfulClientCredentials = _a.contentfulClientCredentials;
    return __awaiter(this, void 0, void 0, function () {
        var client, caseInsensitivePath, _b, slug, pageEntries, webinarsPageEntries, webinarsSectionComponentBlock, webinarFields, webinarPage;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    client = getContentfulClient(!!usePreviewClient, contentfulClientCredentials);
                    caseInsensitivePath = path.toLowerCase();
                    _b = __read(caseInsensitivePath.split('webinars/'), 2), slug = _b[1];
                    if (!path || !slug || slug.length === 0) {
                        return [2 /*return*/, null];
                    }
                    return [4 /*yield*/, getContentfulEntries(client, {
                            content_type: 'page',
                            'fields.path': '/',
                            include: LINK_LEVEL_DEPTH,
                            locale: countryCodeToLocale[countryCode === null || countryCode === void 0 ? void 0 : countryCode.toUpperCase()],
                        })];
                case 1:
                    pageEntries = _c.sent();
                    return [4 /*yield*/, getContentfulEntries(client, {
                            content_type: 'webinar',
                            'fields.slug': slug,
                            include: LINK_LEVEL_DEPTH,
                            locale: countryCodeToLocale[countryCode === null || countryCode === void 0 ? void 0 : countryCode.toUpperCase()],
                        })];
                case 2:
                    webinarsPageEntries = _c.sent();
                    return [4 /*yield*/, getContentfulEntries(client, {
                            content_type: 'componentBlock',
                            'fields.componentName': 'webinarSection',
                            include: LINK_LEVEL_DEPTH,
                            locale: countryCodeToLocale[countryCode === null || countryCode === void 0 ? void 0 : countryCode.toUpperCase()],
                        })];
                case 3:
                    webinarsSectionComponentBlock = _c.sent();
                    if (webinarsPageEntries.total === 0 || pageEntries.total === 0) {
                        return [2 /*return*/, null];
                    }
                    if (webinarsPageEntries.total > 1 || pageEntries.total > 1) {
                        // eslint-disable-next-line no-console
                        console.error('More that 1 page found with the same path');
                    }
                    webinarFields = webinarsPageEntries.items[0].fields;
                    webinarPage = __assign(__assign({}, pageEntries.items[0]), { fields: __assign(__assign(__assign(__assign({}, pageEntries.items[0].fields), ((webinarFields === null || webinarFields === void 0 ? void 0 : webinarFields.title) && {
                            openGraphTitle: webinarFields.title,
                            tabTitle: webinarFields.title,
                        })), ((webinarFields === null || webinarFields === void 0 ? void 0 : webinarFields.abstract) && {
                            metaDescription: webinarFields.abstract,
                            openGraphDescription: webinarFields.abstract,
                        })), { sections: [
                                __assign(__assign({}, webinarsPageEntries.items[0]), { fields: __assign(__assign({}, webinarFields), { componentName: 'webinarPage', textCopies: webinarsSectionComponentBlock.items[0].fields.textCopies }), sys: __assign(__assign({}, webinarsPageEntries.items[0].sys), { contentType: __assign(__assign({}, webinarsPageEntries.items[0].sys.contentType), { sys: __assign(__assign({}, webinarsPageEntries.items[0].sys.contentType.sys), { id: 'componentBlock' }) }) }) }),
                            ] }) });
                    return [2 /*return*/, webinarPage];
            }
        });
    });
}
function getRedirect(_a) {
    var path = _a.path, countryCode = _a.countryCode, usePreviewClient = _a.usePreviewClient, contentfulClientCredentials = _a.contentfulClientCredentials;
    return __awaiter(this, void 0, void 0, function () {
        var caseInsensitivePath, client, redirects;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    caseInsensitivePath = path.toLowerCase();
                    client = getContentfulClient(!!usePreviewClient, contentfulClientCredentials);
                    return [4 /*yield*/, getContentfulEntries(client, {
                            content_type: 'redirect',
                            'fields.path': caseInsensitivePath,
                            include: LINK_LEVEL_DEPTH,
                            locale: countryCodeToLocale[countryCode === null || countryCode === void 0 ? void 0 : countryCode.toUpperCase()],
                        })];
                case 1:
                    redirects = _b.sent();
                    if (redirects.total > 0) {
                        if (redirects.total > 1) {
                            // eslint-disable-next-line no-console
                            console.error('More that 1 page found with the same path');
                        }
                        return [2 /*return*/, redirects.items[0]];
                    }
                    return [2 /*return*/, null];
            }
        });
    });
}
function useGetPage(path, contentfulClientCredentials) {
    var _this = this;
    var contentfulClient = useContentfulClient();
    var _a = __read(React.useState(null), 2), page = _a[0], setPage = _a[1];
    var countryCode = React.useContext(CountryLocationContext).countryCode;
    var loadPage = React.useCallback(function (path) { return __awaiter(_this, void 0, void 0, function () {
        var _a, error_2;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    _b.trys.push([0, 2, , 3]);
                    _a = setPage;
                    return [4 /*yield*/, getPage({ contentfulClientCredentials: contentfulClientCredentials, countryCode: countryCode, path: path, usePreviewClient: !!contentfulClient })];
                case 1:
                    _a.apply(void 0, [_b.sent()]);
                    return [3 /*break*/, 3];
                case 2:
                    error_2 = _b.sent();
                    // eslint-disable-next-line no-console
                    console.log(error_2);
                    return [3 /*break*/, 3];
                case 3: return [2 /*return*/];
            }
        });
    }); }, [countryCode, contentfulClient, contentfulClientCredentials]);
    React.useEffect(function () {
        loadPage(path);
    }, [path, loadPage]);
    return {
        page: page,
    };
}
function getPages(contentfulClientCredentials) {
    return __awaiter(this, void 0, void 0, function () {
        var client, pageEntries, result, pageEntries_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    client = getContentfulClient(false, contentfulClientCredentials);
                    return [4 /*yield*/, getContentfulEntries(client, {
                            content_type: 'page',
                            include: 1,
                        })];
                case 1:
                    pageEntries = _a.sent();
                    result = pageEntries.items;
                    _a.label = 2;
                case 2:
                    if (!(result.length < pageEntries.total)) return [3 /*break*/, 4];
                    return [4 /*yield*/, getContentfulEntries(client, {
                            content_type: 'page',
                            include: 1,
                            skip: result.length,
                        })];
                case 3:
                    pageEntries_1 = _a.sent();
                    result = __spreadArray(__spreadArray([], __read(result), false), __read(pageEntries_1.items), false);
                    return [3 /*break*/, 2];
                case 4: return [2 /*return*/, result];
            }
        });
    });
}
function generateSiteMap(contentfulClientCredentials) {
    return __awaiter(this, void 0, void 0, function () {
        var pages;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, getPages(contentfulClientCredentials)];
                case 1:
                    pages = _a.sent();
                    return [2 /*return*/, { pages: pages }];
            }
        });
    });
}

export { useContentful as default, generateSiteMap, getContentfulClient, getContentfulEntries, getGlobalTextCopy, getPage, getPages, getRedirect, getVideoEntry, getWebinarsEntry, useContentful, useGetPage };
//# sourceMappingURL=useContentful.esm.js.map
